import { drop, flatten, takeWhile, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { Truss } from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import { naturalSortByProperty, notNullish, sortBy } from '@paragon/utilities';

import { WidthCache } from '../ComponentGeometryCaches';
import { RootState } from '../store';
import { getProductionComponentInstancesByBatchGuid } from './batches';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab } from './getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid } from './getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid';
import { getLoadedTrussesByAnalysisSetGuid } from './trusses';

export interface ProductionGroupGridRow {
  readonly analysisSetGuid: string;
  readonly truss?: Truss; // undefined if truss is loading
  readonly quantity: number;
}

export const getAnalysisSetGuidToQuantityForUnassigned = createSelector(
  getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTab,
  (state: RootState) => state.batchesAndBundlesTab.activeList,

  (productionComponentInstancesForActiveProductionGroup, creating) => {
    const unassignedProductionComponentInstances = productionComponentInstancesForActiveProductionGroup.filter(
      (productionComponentInstance) =>
        creating === 'batches'
          ? productionComponentInstance.batchGuid === null
          : productionComponentInstance.bundleGuid === null,
    );
    return unassignedProductionComponentInstances.reduce(
      (quantityMap, productionComponentInstance) => {
        productionComponentInstance.analysisSetGuid in quantityMap
          ? (quantityMap[productionComponentInstance.analysisSetGuid] += 1)
          : (quantityMap[productionComponentInstance.analysisSetGuid] = 1);
        return quantityMap;
      },
      {} as { [analysisSetGuid: string]: number },
    );
  },
);

export const getProductionGroupGridRows = createSelector(
  getAnalysisSetGuidToQuantityForUnassigned,
  getLoadedTrussesByAnalysisSetGuid,

  (getAnalysisSetGuidToQuantityForUnassigned, loadedTrussesByAnalysisSetGuid): ProductionGroupGridRow[] =>
    Object.entries(getAnalysisSetGuidToQuantityForUnassigned).map(([analysisSetGuid, quantity]) => ({
      analysisSetGuid,
      truss: loadedTrussesByAnalysisSetGuid[analysisSetGuid],
      quantity,
    })),
);

/** The user has checked a number of components. The order of the components in the array in state is not meaningful
 *  since it is based on whatever order the user checked the components in. We want the order of the components to
 *  reflect the ordering in the grid, which is currently first by batch ascending and then by width descending. Presently
 *  the user is not able to edit the sorting (see ProductionGroupGrid.tsx), but if they ever are, this will need to
 *  change.
 */
export const getOrderedAnalysisSetGuidsForUnbundledInActiveProductionGroup = createSelector(
  getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid,
  (state: RootState) => state.batches,
  getLoadedTrussesByAnalysisSetGuid,

  (
    productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid,
    batches,
    loadedTrussesByAnalysisSetGuid,
  ) => {
    const analysisSetGuidsByBatchGuid = uniq(
      flatten(Object.values(productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid)).map(
        ({ analysisSetGuid }) => analysisSetGuid,
      ),
    ).reduce(
      (accumulator, analysisSetGuid) => {
        const batchGuids = uniq(
          productionComponentInstancesForActiveProductionGroupNotBundledByAnalysisSetGuid[analysisSetGuid]
            .map((productionComponentInstance) => productionComponentInstance.batchGuid)
            .filter(notNullish),
        );
        if (batchGuids.length > 1)
          throw new Error('An analysisSetGuid belonging to more than one batch is not presently supported');
        if (batchGuids.length === 0) return accumulator;
        accumulator[batchGuids[0]] = (accumulator[batchGuids[0]] ?? []).concat([analysisSetGuid]);
        return accumulator;
      },
      {} as { [batchGuid: string]: readonly string[] },
    );

    const batchGuidsOrderedById = sortBy(
      Object.keys(analysisSetGuidsByBatchGuid),
      (batchGuid) => batches[batchGuid].id,
    );

    return batchGuidsOrderedById.flatMap((batchGuid) => {
      const sortableComponentsWithinBatch = analysisSetGuidsByBatchGuid[batchGuid]
        .filter((analysisSetGuid) => loadedTrussesByAnalysisSetGuid[analysisSetGuid])
        .map((analysisSetGuid) => ({
          analysisSetGuid,
          width: WidthCache.get(analysisSetGuid, loadedTrussesByAnalysisSetGuid[analysisSetGuid]),
          name: loadedTrussesByAnalysisSetGuid[analysisSetGuid].name,
        }));

      // Sort first by width descending. For components of the same width, natural sort by name ascending.
      let orderedAnalysisSetGuidsWithinBatch: string[] = [];
      const sortableComponentsOrderedByWidthDescending = sortBy(
        sortableComponentsWithinBatch,
        (sortableComponent) => sortableComponent.width,
        'desc',
      );
      let i = 0;
      while (i < sortableComponentsOrderedByWidthDescending.length) {
        const currentWidth = sortableComponentsOrderedByWidthDescending[i].width;
        const componentsOfSameWidth = takeWhile(
          drop(sortableComponentsOrderedByWidthDescending, i),
          (sortableComponent) => sortableComponent.width === currentWidth,
        );
        const sortedComponentsOfSameWidth = naturalSortByProperty(
          componentsOfSameWidth,
          (sortableComponent) => sortableComponent.name,
        );
        orderedAnalysisSetGuidsWithinBatch = orderedAnalysisSetGuidsWithinBatch.concat(
          sortedComponentsOfSameWidth.map(({ analysisSetGuid }) => analysisSetGuid),
        );
        i += sortedComponentsOfSameWidth.length;
      }
      return orderedAnalysisSetGuidsWithinBatch;
    });
  },
);

export const getOrderedCheckedAnalysisSetGuidsForUnbundled = createSelector(
  getOrderedAnalysisSetGuidsForUnbundledInActiveProductionGroup,
  (state: RootState) => state.batchesAndBundlesTab.checkedAnalysisSetGuids,

  (orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup, checkedAnalysisSetGuids) =>
    orderedAnalysisSetGuidsForUnbundledInActiveProductionGroup.filter((analysisSetGuid) =>
      checkedAnalysisSetGuids.includes(analysisSetGuid),
    ),
);

export const getBatchCreationOrAdditionWouldHaveMixedThicknesses = createSelector(
  (state: RootState) => state.batchesAndBundlesTab.checkedAnalysisSetGuids,
  getLoadedTrussesByAnalysisSetGuid,
  (state: RootState) => state.batchesAndBundlesTab.batchBeingEditedGuid,
  getProductionComponentInstancesByBatchGuid,

  (
    checkedAnalysisSetGuids,
    loadedTrussesByAnalysisSetGuid,
    batchBeingEditedGuid,
    productionComponentInstancesByBatchGuid,
  ) => {
    let trusses = checkedAnalysisSetGuids
      .map((analysisSetGuid) => loadedTrussesByAnalysisSetGuid[analysisSetGuid])
      .filter(notNullish);
    if (batchBeingEditedGuid !== null) {
      const existingProductionComponentInstances = productionComponentInstancesByBatchGuid[batchBeingEditedGuid];
      const existingTrusses = uniq(
        existingProductionComponentInstances.map(
          (productionComponentInstance) => productionComponentInstance.analysisSetGuid,
        ),
      )
        .map((analysisSetGuid) => loadedTrussesByAnalysisSetGuid[analysisSetGuid])
        .filter(notNullish);
      trusses = trusses.concat(existingTrusses);
    }

    const componentThicknesses: number[] = [];
    for (const truss of trusses) {
      if (!componentThicknesses.some((thickness) => distanceEquals(thickness, truss.thickness))) {
        componentThicknesses.push(truss.thickness);
      }
    }
    return componentThicknesses.length > 1;
  },
);
